import { useState } from "react";
import { MapPin, Printer, Trash2, ZoomIn, X, Archive, RotateCcw, BadgeCheck } from "lucide-react";
import { fileUrl, formatTL, formatDate } from "@/lib/api";
import { FACADES, VIEWS, STATUS_LABEL, labelList } from "@/lib/portfolioOptions";
import { StaticLocationMap } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LinkedDocuments } from "@/components/scan/LinkedDocuments";
import { DirectionsButton } from "@/components/route/DirectionsDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Fact = ({ label, value, accent }) => (
  <div className="p-3 rounded-lg bg-slate-50">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-bold ${accent ? "text-emerald-700 text-lg" : "text-slate-900"}`}>{value || "-"}</div>
  </div>
);

export const PortfolioPresentation = ({ item, canManage, onClose, onEdit, onDelete, onStatus }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!item) return null;
  const imgs = item.image_paths || [];
  const floor = item.floor ? `${item.floor}${item.total_floors ? ` / ${item.total_floors} katlı` : ""}` : null;

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="portfolio-presentation-modal"
          onInteractOutside={(e) => lightbox && e.preventDefault()} onEscapeKeyDown={(e) => { if (lightbox) { e.preventDefault(); setLightbox(null); } }}>
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={item.listing_type === "SALE" ? "bg-emerald-600" : "bg-blue-600"}>{item.listing_type === "SALE" ? "Satılık" : "Kiralık"}</Badge>
              {item.archived && <Badge variant="outline" data-testid="presentation-archived-badge" className="border-amber-400 text-amber-700"><Archive className="w-3 h-3 mr-1" />Arşiv · {STATUS_LABEL[item.status]}</Badge>}
              {item.has_eids && <Badge variant="outline" className="border-emerald-400 text-emerald-700"><BadgeCheck className="w-3 h-3 mr-1" />EİDS: {item.eids_code}</Badge>}
            </div>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>{item.title}</DialogTitle>
            <div className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{item.neighborhood ? `${item.neighborhood}, ` : ""}{item.district}, {item.city}</div>
          </DialogHeader>

          {imgs.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-4 aspect-video rounded-lg overflow-hidden bg-slate-100 relative group cursor-zoom-in" onClick={() => setLightbox(imgs[0])}>
                <img src={fileUrl(imgs[0], "lg")} srcSet={`${fileUrl(imgs[0], "md")} 960w, ${fileUrl(imgs[0], "lg")} 1600w`} sizes="(max-width: 768px) 100vw, 60vw" alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center"><ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100" /></div>
              </div>
              {imgs.slice(1).map((id) => (
                <div key={id} data-testid="presentation-thumb" className="aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-zoom-in hover:ring-2 hover:ring-emerald-500" onClick={() => setLightbox(id)}>
                  <img src={fileUrl(id, "sm")} loading="lazy" alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Fact label="Fiyat" value={formatTL(item.price)} accent />
            <Fact label="Oda" value={item.rooms} />
            <Fact label="Alan" value={`${item.area_m2} m²`} />
            <Fact label="Kat" value={floor} />
            <Fact label="Cephe" value={labelList(item.facades, FACADES)} />
            <Fact label="Manzara" value={labelList(item.views, VIEWS)} />
            <Fact label="EİDS" value={item.has_eids ? item.eids_code : "Yok"} />
            <Fact label="Durum" value={STATUS_LABEL[item.status]} />
          </div>

          {item.parcel && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="presentation-parcel-facts">
              <Fact label="Ada / Parsel" value={`${item.parcel.ada} / ${item.parcel.parsel}`} />
              <Fact label="Mahalle" value={item.parcel.mahalle} />
              <Fact label="Tapu Yüzölçümü" value={item.parcel.alan_text ? `${item.parcel.alan_text} m²` : null} />
              <Fact label="Nitelik" value={item.parcel.nitelik} />
            </div>
          )}
          {item.lat != null && (
            <div className="space-y-2">
              <StaticLocationMap lat={item.lat} lng={item.lng} geometry={item.parcel?.geometry} />
              <DirectionsButton testid="presentation-directions-btn" size="sm" target={{ lat: item.lat, lng: item.lng, geometry: item.parcel?.geometry, label: `${item.title} · ${item.district}, ${item.city}` }} />
            </div>
          )}

          {item.description && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Açıklama</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.description}</p>
            </div>
          )}

          <LinkedDocuments portfolioId={item.id} />

          <div className="flex justify-between items-center pt-4 border-t flex-wrap gap-3">
            <div className="text-xs text-slate-500">
              Danışman: <span className="font-semibold text-slate-900">{item.owner_name}</span>
              {item.archived_at && <> · Arşive alındı: {formatDate(item.archived_at)}</>}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-2" />Yazdır</Button>
              {canManage && (item.archived ? (
                <Button variant="outline" data-testid="reactivate-portfolio-btn" onClick={() => onStatus("ACTIVE")}><RotateCcw className="w-4 h-4 mr-2" />Tekrar Yayına Al</Button>
              ) : (
                <Button data-testid="mark-closed-btn" className="bg-amber-600 hover:bg-amber-700" onClick={() => onStatus(item.listing_type === "SALE" ? "SOLD" : "RENTED")}>
                  <Archive className="w-4 h-4 mr-2" />{item.listing_type === "SALE" ? "Satıldı" : "Kiralandı"} olarak işaretle
                </Button>
              ))}
              {canManage && <Button variant="outline" data-testid="edit-portfolio-btn" onClick={onEdit}>Düzenle</Button>}
              {canManage && <Button variant="destructive" data-testid="delete-portfolio-btn" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div data-testid="portfolio-lightbox" className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-6 cursor-zoom-out" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white hover:text-emerald-400"><X className="w-8 h-8" /></button>
          <img src={fileUrl(lightbox)} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
};
